import {
  assignedUserDisplayName,
  CardFeedCard,
  CardFeedCardStatus,
  CardFeedCardStatusFilter,
  isInviteSentCardFeedCard,
} from 'src/queries/cardFeeds/cardFeeds.models'

// The owner display name for search + sort, derived from the assigned user
// (there is no separate stored cardholder field).
const ownerName = (card: CardFeedCard): string | null =>
  assignedUserDisplayName(card.assignedUser)

export type CardFeedSortField =
  | 'cardName'
  | 'cardHolder'
  | 'status'
  | 'dateAdded'

/**
 * Drives all sort/filter/search for the card-feed table. Field names mirror the
 * eventual server request shape (cf. VirtualCardsFiltersBody:
 * search / statuses / sortField / sortDirection) so a future move to
 * server-side filtering is a localized swap — see the design spec.
 */
export interface CardFeedCardsView {
  search?: string
  statuses?: CardFeedCardStatusFilter[]
  sortField?: CardFeedSortField
  sortDirection?: 'ASC' | 'DESC'
}

// Logical ordering for the Status column — not alphabetical. Unassigned first
// (assignable), then Pending and Failed entries that may need admin follow-up,
// then Assigned (done).
const STATUS_RANK: Record<CardFeedCardStatus, number> = {
  UNASSIGNED: 0,
  PENDING: 1,
  FAILED: 2,
  ASSIGNED: 3,
}

const matchesSearch = (card: CardFeedCard, term: string): boolean => {
  const haystack = [card.cardName, card.lastFour, ownerName(card) ?? '']
    .join(' ')
    .toLowerCase()
  return haystack.includes(term)
}

const matchesStatusFilter = (
  card: CardFeedCard,
  filter: CardFeedCardStatusFilter,
): boolean => {
  if (filter === 'PENDING_INVITE_SENT') {
    return isInviteSentCardFeedCard(card)
  }
  if (filter === 'PENDING') {
    return card.status === 'PENDING' && !isInviteSentCardFeedCard(card)
  }
  return card.status === filter
}

const compareByField = (
  a: CardFeedCard,
  b: CardFeedCard,
  field: CardFeedSortField,
): number => {
  switch (field) {
    case 'dateAdded':
      return new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime()
    case 'status':
      return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    case 'cardHolder': {
      // Empty/null owner sorts last in ascending order.
      const av = ownerName(a) ?? '￿'
      const bv = ownerName(b) ?? '￿'
      return av.toLowerCase().localeCompare(bv.toLowerCase())
    }
    case 'cardName':
    default:
      return a.cardName.toLowerCase().localeCompare(b.cardName.toLowerCase())
  }
}

export const selectVisibleCards = (
  cards: CardFeedCard[],
  view: CardFeedCardsView,
): CardFeedCard[] => {
  let result = cards

  const term = view.search?.trim().toLowerCase()
  if (term) {
    result = result.filter((card) => matchesSearch(card, term))
  }

  if (view.statuses && view.statuses.length > 0) {
    result = result.filter((card) =>
      view.statuses?.some((status) => matchesStatusFilter(card, status)),
    )
  }

  if (view.sortField) {
    const direction = view.sortDirection === 'DESC' ? -1 : 1
    // Copy before sort — never mutate the caller's array (React Query cache).
    result = [...result].sort((a, b) => {
      const primary =
        compareByField(a, b, view.sortField as CardFeedSortField) * direction
      // Ties fall back to card name, always A→Z regardless of direction, so
      // groups (e.g. all UNASSIGNED cards) read alphabetically (EX-39728).
      return primary !== 0 ? primary : compareByField(a, b, 'cardName')
    })
  }

  return result
}
